/**
 * Production-Grade Logger Service
 * 
 * A sophisticated logging system that:
 * - Provides structured logging with log levels
 * - Automatically includes context (user, timestamp, session)
 * - Integrates with external services (Sentry, LogRocket) in production
 * - Filters logs based on environment
 * - Batches logs for performance
 * - Supports log sampling for high-traffic scenarios
 * 
 * Usage:
 * ```typescript
 * logger.info('User completed onboarding', { userId: user.id, step: 'laser-assessment' });
 * logger.error('Payment failed', { orderId, error }, error);
 * logger.warn('API rate limit approaching', { endpoint: '/api/nitara/chat', usage: '90%' });
 * ```
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';
type LogContext = Record<string, unknown>;

interface LogEntry {
  level: LogLevel;
  message: string;
  context?: LogContext;
  error?: Error;
  timestamp: string;
  userId?: string;
  sessionId?: string;
  environment: string;
  url?: string;
  userAgent?: string;
}

interface LoggerConfig {
  minLevel: LogLevel;
  enableConsole: boolean;
  enableBatching: boolean;
  batchSize: number;
  batchInterval: number;
  sampleRate: number; // 0-1, for high-volume scenarios
  enableSentry: boolean;
  enableRemote: boolean;
  remoteEndpoint?: string;
}

class LoggerService {
  private config: LoggerConfig;
  private logQueue: LogEntry[] = [];
  private flushTimer: NodeJS.Timeout | null = null;
  private sessionId: string;
  private levels: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
    fatal: 4
  };

  constructor() {
    this.sessionId = this.generateSessionId();
    
    // Environment-based configuration
    const isDevelopment = import.meta.env.DEV;
    const isProduction = import.meta.env.PROD;
    
    this.config = {
      minLevel: isDevelopment ? 'debug' : 'info',
      enableConsole: isDevelopment,
      enableBatching: isProduction,
      batchSize: 50,
      batchInterval: 10000, // 10 seconds
      sampleRate: isProduction ? 0.1 : 1.0, // Sample 10% in prod, 100% in dev
      enableSentry: isProduction && !!import.meta.env.VITE_SENTRY_DSN,
      enableRemote: isProduction && !!import.meta.env.VITE_LOGGING_ENDPOINT,
      remoteEndpoint: import.meta.env.VITE_LOGGING_ENDPOINT
    };

    // Initialize Sentry if enabled
    if (this.config.enableSentry) {
      this.initializeSentry();
    }

    // Start flush timer if batching enabled
    if (this.config.enableBatching) {
      this.startFlushTimer();
    }

    // Flush on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flush());
    }
  }

  /**
   * Debug level logging (development only)
   */
  debug(message: string, context?: LogContext): void {
    this.log('debug', message, context);
  }

  /**
   * Info level logging
   */
  info(message: string, context?: LogContext): void {
    this.log('info', message, context);
  }

  /**
   * Warning level logging
   */
  warn(message: string, context?: LogContext, error?: Error): void {
    this.log('warn', message, context, error);
  }

  /**
   * Error level logging
   */
  error(message: string, context?: LogContext, error?: Error): void {
    this.log('error', message, context, error);
    
    // Send to Sentry immediately for errors
    if (this.config.enableSentry && error) {
      this.sendToSentry(error, { message, ...context });
    }
  }

  /**
   * Fatal level logging (critical errors)
   */
  fatal(message: string, context?: LogContext, error?: Error): void {
    this.log('fatal', message, context, error);
    
    // Immediate flush for fatal errors
    this.flush();
    
    if (this.config.enableSentry && error) {
      this.sendToSentry(error, { message, ...context, level: 'fatal' });
    }
  }

  /**
   * Core logging method
   */
  private log(level: LogLevel, message: string, context?: LogContext, error?: Error): void {
    // Check if level meets threshold
    if (this.levels[level] < this.levels[this.config.minLevel]) {
      return;
    }

    // Apply sampling for high-volume scenarios
    if (Math.random() > this.config.sampleRate) {
      return;
    }

    const entry: LogEntry = {
      level,
      message,
      context,
      error,
      timestamp: new Date().toISOString(),
      userId: this.getCurrentUserId(),
      sessionId: this.sessionId,
      environment: import.meta.env.MODE,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
    };

    // Console output in development
    if (this.config.enableConsole) {
      this.logToConsole(entry);
    }

    // Queue for batching or send immediately
    if (this.config.enableBatching) {
      this.logQueue.push(entry);
      
      // Flush if batch size reached
      if (this.logQueue.length >= this.config.batchSize) {
        this.flush();
      }
    } else if (this.config.enableRemote) {
      this.sendToRemote([entry]);
    }
  }

  /**
   * Console output with formatting
   */
  private logToConsole(entry: LogEntry): void {
    const prefix = `[${entry.level.toUpperCase()}]`;
    const contextStr = entry.context ? JSON.stringify(entry.context, null, 2) : '';
    
    const styles: Record<LogLevel, string> = {
      debug: 'color: #6b7280',
      info: 'color: #3b82f6',
      warn: 'color: #f59e0b',
      error: 'color: #ef4444',
      fatal: 'color: #dc2626; font-weight: bold'
    };

    console.log(
      `%c${prefix} ${entry.message}`,
      styles[entry.level],
      contextStr
    );

    if (entry.error) {
      console.error(entry.error);
    }
  }

  /**
   * Flush queued logs to remote
   */
  private async flush(): Promise<void> {
    if (this.logQueue.length === 0) return;

    const logsToSend = [...this.logQueue];
    this.logQueue = [];

    if (this.config.enableRemote) {
      await this.sendToRemote(logsToSend);
    }
  }

  /**
   * Send logs to remote endpoint
   */
  private async sendToRemote(logs: LogEntry[]): Promise<void> {
    if (!this.config.remoteEndpoint) return;

    try {
      await fetch(this.config.remoteEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ logs }),
        keepalive: true
      });
    } catch (error) {
      // Silently fail - don't want logging to break the app
      if (this.config.enableConsole) {
        console.warn('[Logger] Failed to send logs to remote:', error);
      }
    }
  }

  /**
   * Initialize Sentry integration
   */
  private initializeSentry(): void {
    // Sentry initialization would go here
    // This is a placeholder for actual Sentry SDK integration
    // Example:
    // import * as Sentry from '@sentry/react';
    // Sentry.init({ dsn: import.meta.env.VITE_SENTRY_DSN });
  }

  /**
   * Send error to Sentry
   */
  private sendToSentry(error: Error, context?: Record<string, unknown>): void {
    // Actual Sentry error capture would go here
    // Example:
    // import * as Sentry from '@sentry/react';
    // Sentry.captureException(error, { extra: context });
  }

  /**
   * Start periodic flush timer
   */
  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.config.batchInterval);
  }

  /**
   * Get current user ID from auth service
   */
  private getCurrentUserId(): string | undefined {
    try {
      // Avoid circular dependency - check localStorage directly
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id;
      }
    } catch {
      // Ignore parsing errors
    }
    return undefined;
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Update configuration dynamically
   */
  updateConfig(updates: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...updates };
  }

  /**
   * Get current configuration
   */
  getConfig(): Readonly<LoggerConfig> {
    return { ...this.config };
  }

  /**
   * Cleanup method
   */
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.flush();
  }
}

// Singleton instance
const logger = new LoggerService();

export default logger;
export type { LogLevel, LogContext, LogEntry, LoggerConfig };
